'use client';

import { useId } from 'react';
import { format, isValid, parse, parseISO } from 'date-fns';
import { DateField } from '@/components/ui/DateField';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { Input } from '@/components/ui/Input';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { CountryField, RegionField, EducationField, DateOfBirthField } from '@/components/forms/ProfileFields';
import SpecialtiesField, { SpecialtiesValue } from '@/components/forms/SpecialtiesField';
import { HealthcareRoleField } from '@/components/forms/HealthcareRoleField';
import { NotWorkingCheckbox } from '@/components/forms/NotWorkingCheckbox';
import { specialtyHiddenFor, type HealthcareRole, type SpecialtyOption } from '@/lib/specialties/types';
import type { AdditionalField } from '@/lib/profile/completion';

export interface ProfileFieldInputProps {
  field: AdditionalField;
  /** Drives the asterisk – never field.is_required, which is a snapshot of the stored role. */
  required: boolean;
  values: Record<string, string>;
  /** Translation key of what is wrong with this field. */
  error?: string;
  specialties: SpecialtyOption[];
  language: 'uk' | 'en';
  /** A country change also clears the region – the parent does that. */
  onChange: (name: string, value: string) => void;
  onSpecialtiesChange: (value: SpecialtiesValue) => void;
}

const parseStoredDate = (value?: string): Date | null => {
  if (!value) return null;
  const trimmed = value.trim();

  try {
    const isoParsed = parseISO(trimmed);
    if (isValid(isoParsed)) {
      return isoParsed;
    }
  } catch {
    // Ignore parse errors for non-ISO strings
  }

  const ymdParsed = parse(trimmed, 'yyyy-MM-dd', new Date());
  if (isValid(ymdParsed)) {
    return ymdParsed;
  }

  const europeanParsed = parse(trimmed, 'dd.MM.yyyy', new Date());
  if (isValid(europeanParsed)) {
    return europeanParsed;
  }

  const nativeDate = new Date(trimmed);
  return isValid(nativeDate) ? nativeDate : null;
};

function specialtiesValueOf(values: Record<string, string>): SpecialtiesValue {
  let secondary: string[] = [];
  try {
    const parsed = JSON.parse(values.secondary_specialty_ids || '[]');
    if (Array.isArray(parsed)) secondary = parsed;
  } catch {
    secondary = [];
  }
  return {
    primary: values.specialty_id || null,
    secondary,
    other: values.specialty_other || '',
  };
}

export default function ProfileFieldInput({
  field,
  required,
  values,
  error: errorKey,
  specialties,
  language,
  onChange,
  onSpecialtiesChange,
}: ProfileFieldInputProps) {
  const { t } = useLanguage();
  const uid = useId();
  const inputId = `${uid}-${field.field_name}`;
  const errorId = `${inputId}-error`;
  const input = renderInput();
  if (!input) return null;
  return <div data-field-name={field.field_name}>{input}</div>;

  function renderInput() {
    const value = values[field.field_name] || '';
    const error = errorKey ? t(errorKey) : undefined;
    const label = language === 'uk'
      ? (field.label_ua || field.field_name)
      : (field.label_en || field.label_ua || field.field_name);
    const placeholder = language === 'uk'
      ? (field.placeholder_ua || '')
      : (field.placeholder_en || field.placeholder_ua || '');

    const commonClasses = `w-full px-3 py-2 border rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500 ${
      error ? 'border-red-500' : 'border-gray-300'
    }`;

    // Shared profile field components (searchable, sorted)
    if (field.field_name === 'country') {
      return (
        <CountryField
          value={value}
          onChange={(v) => onChange('country', v)}
          language={language}
          t={t}
          error={error}
          required={required}
        />
      );
    }

    if (field.field_name === 'region') {
      const countryValue = values['country'] || '';
      return (
        <RegionField
          value={value}
          onChange={(v) => onChange('region', v)}
          countryValue={countryValue}
          language={language}
          t={t}
          error={error}
          required={required}
        />
      );
    }

    if (field.field_name === 'healthcare_role') {
      return (
        <HealthcareRoleField
          value={(values.healthcare_role || null) as HealthcareRole | null}
          onChange={(role) => onChange('healthcare_role', role)}
          required={required}
          error={error}
        />
      );
    }

    if (field.field_name === 'specialty_id') {
      const currentRole = (values.healthcare_role || null) as HealthcareRole | null;
      if (specialtyHiddenFor(currentRole)) {
        return null;
      }
      return (
        <SpecialtiesField
          value={specialtiesValueOf(values)}
          onChange={onSpecialtiesChange}
          options={specialties}
          role={currentRole}
          // field.is_required is a
          // snapshot of the STORED role, so the asterisk has to follow the
          // role picked right now, not the one the fetch saw.
          required={required}
          error={error}
          compact
        />
      );
    }

    if (field.field_name === 'is_not_working') {
      return (
        <div className="space-y-2">
          <NotWorkingCheckbox
            checked={values.is_not_working === 'true'}
            onChange={(checked) => onChange('is_not_working', checked ? 'true' : '')}
            ariaInvalid={!!error}
            ariaDescribedBy={error ? errorId : undefined}
          />
          {error && <p id={errorId} role="alert" className="text-red-500 text-sm">{error}</p>}
        </div>
      );
    }

    if ((field.field_name === 'workplace' || field.field_name === 'position') && values.is_not_working === 'true') {
      return null;
    }

    if (field.field_name === 'education') {
      return (
        <EducationField
          value={value}
          onChange={(v) => onChange('education', v)}
          otherValue={values.education_other || ''}
          onOtherChange={(v) => onChange('education_other', v)}
          language={language}
          t={t}
          error={error}
          required={required}
        />
      );
    }

    if (field.field_name === 'date_of_birth') {
      return (
        <DateOfBirthField
          value={parseStoredDate(value)}
          onChange={(date) => onChange(field.field_name, date ? format(date, 'yyyy-MM-dd') : '')}
          label={label}
          error={error}
          required={required}
          placeholder={language === 'uk' ? 'дд.мм.рррр' : 'dd.mm.yyyy'}
        />
      );
    }

    switch (field.field_type) {
      case 'textarea':
        return (
          <div className="space-y-2">
            <label htmlFor={inputId} className="block text-sm font-semibold text-gray-700">
              {label}
              {required && <span className="text-red-500">*</span>}
            </label>
            <textarea
              id={inputId}
              aria-invalid={error ? 'true' : undefined}
              aria-describedby={error ? errorId : undefined}
              value={value}
              onChange={(e) => onChange(field.field_name, e.target.value)}
              placeholder={placeholder}
              rows={3}
              className={commonClasses}
            />
            {error && <p id={errorId} role="alert" className="text-red-500 text-sm">{error}</p>}
          </div>
        );

      case 'select':
        return (
          <div className="space-y-2">
            <label htmlFor={inputId} className="block text-sm font-semibold text-gray-700">
              {label}
              {required && <span className="text-red-500">*</span>}
            </label>
            <select
              id={inputId}
              aria-invalid={error ? 'true' : undefined}
              aria-describedby={error ? errorId : undefined}
              value={value}
              onChange={(e) => onChange(field.field_name, e.target.value)}
              className={commonClasses}
            >
              <option value="">{t('additionalFields.selectOption')}</option>
              {field.field_options?.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {(language !== 'uk' && option.label_en) ? option.label_en : option.label_ua}
                </option>
              ))}
            </select>
            {error && <p id={errorId} role="alert" className="text-red-500 text-sm">{error}</p>}
          </div>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            <span id={`${inputId}-label`} className="block text-sm font-semibold text-gray-700">
              {label}
              {required && <span className="text-red-500">*</span>}
            </span>
            <div role="radiogroup" aria-labelledby={`${inputId}-label`} aria-describedby={error ? errorId : undefined} className="space-y-2">
              {field.field_options?.options.map((option) => (
                <label key={option.value} className="flex items-center">
                  <input
                    type="radio"
                    name={field.field_name}
                    value={option.value}
                    checked={value === option.value}
                    onChange={(e) => onChange(field.field_name, e.target.value)}
                    className="mr-2"
                  />
                  {(language !== 'uk' && option.label_en) ? option.label_en : option.label_ua}
                </label>
              ))}
            </div>
            {error && <p id={errorId} role="alert" className="text-red-500 text-sm">{error}</p>}
          </div>
        );

      case 'checkbox':
        return (
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                id={inputId}
                aria-invalid={error ? 'true' : undefined}
                aria-describedby={error ? errorId : undefined}
                checked={value === 'true'}
                onChange={(e) => onChange(field.field_name, e.target.checked ? 'true' : 'false')}
                className="mr-2"
              />
              {label}
              {required && <span className="text-red-500">*</span>}
            </label>
            {error && <p id={errorId} role="alert" className="text-red-500 text-sm">{error}</p>}
          </div>
        );

      case 'date':
        return (
          <div className="space-y-2">
            <label htmlFor={inputId} className="block text-sm font-semibold text-gray-700">
              {label}
              {required && <span className="text-red-500">*</span>}
            </label>
            <DateField
              id={inputId}
              value={parseStoredDate(value)}
              onChange={(date) => onChange(field.field_name, date ? format(date, 'yyyy-MM-dd') : '')}
              placeholder={placeholder || 'дд.мм.рррр'}
              className="w-full"
              allowClear={!required}
              ariaInvalid={!!error}
              ariaDescribedBy={error ? errorId : undefined}
            />
            {error && <p id={errorId} role="alert" className="text-red-500 text-sm">{error}</p>}
          </div>
        );

      case 'number':
        return (
          <Input
            id={inputId}
            type="number"
            value={value}
            onChange={(e) => onChange(field.field_name, e.target.value)}
            placeholder={placeholder}
            label={label}
            error={error}
            required={required}
          />
        );

      case 'phone':
        return (
          <PhoneInput
            id={inputId}
            value={value}
            onChange={(v) => onChange(field.field_name, v)}
            placeholder={placeholder}
            label={label}
            required={required}
            error={error}
          />
        );

      default:
        return (
          <Input
            id={inputId}
            type={field.field_type === 'email' ? 'email' : 'text'}
            value={value}
            onChange={(e) => onChange(field.field_name, e.target.value)}
            placeholder={placeholder}
            label={label}
            error={error}
            required={required}
          />
        );
    }
  }
}
